import type { ChipTone } from "@components/cockpit";

/**
 * Shared schema for the dashboard methodology guides (Risk, Signals, …).
 *
 * Goal: every tile answers the SAME questions in the SAME order so the modal reads like a
 * reference, not a wall of prose. Three orienting BADGES say "what am I looking at" at a glance —
 *   Subject    : whose number is this — our book, the market, a forward scenario, a reconciliation?
 *   TimeBasis  : how fresh — live, close-confirmed, a daily snapshot, or a backtest (hypothetical)?
 *   Role       : what does it CONTROL — sizing/the cap, situational awareness, info only, official record?
 * — then a consistent body (what it is / tells you / moves it / how to read / the math / limits / read-with / source).
 *
 * All the new fields are OPTIONAL so content can be migrated panel-by-panel: an un-migrated metric
 * still renders its legacy why/how, a migrated one renders the full rich card. Presentation only —
 * this file defines types + the badge label/tone maps; no data, no logic.
 */

// ---- Badge taxonomies -------------------------------------------------------

export type SubjectBadge = "book" | "market" | "forward" | "reconciliation";
export type TimeBasisBadge = "live" | "close" | "snapshot" | "backtest";
export type RoleBadge = "drives" | "situational" | "informational" | "official";

export const SUBJECT_META: Record<SubjectBadge, { label: string; tone: ChipTone; hint: string }> = {
  book: { label: "Our book", tone: "info", hint: "Describes the positions Helion actually holds." },
  market: { label: "Market", tone: "neutral", hint: "External market state — not our positions." },
  forward: { label: "Forward · scenario", tone: "warning", hint: "Modeled / hypothetical — a projection, not a realized number." },
  reconciliation: { label: "Reconciliation", tone: "info", hint: "Compares the book against the official record." },
};

export const TIME_BASIS_META: Record<TimeBasisBadge, { label: string; tone: ChipTone; hint: string }> = {
  live: { label: "Live", tone: "positive", hint: "Refreshed intraday from the live feed." },
  close: { label: "Close-confirmed", tone: "neutral", hint: "From the last confirmed market close, not jittery intraday." },
  snapshot: { label: "Daily snapshot", tone: "neutral", hint: "From the most recent daily snapshot row." },
  backtest: { label: "Backtest · hypothetical", tone: "warning", hint: "Backtested / hypothetical; not live performance." },
};

export const ROLE_META: Record<RoleBadge, { label: string; tone: ChipTone; hint: string }> = {
  drives: { label: "Drives sizing / cap", tone: "elevated", hint: "Feeds the deployment / exposure-cap logic." },
  situational: { label: "Situational", tone: "info", hint: "Awareness only — does not auto-drive trades." },
  informational: { label: "Informational", tone: "neutral", hint: "Context only; no action attached." },
  official: { label: "Official record", tone: "positive", hint: "An official figure (e.g. NAV Fund Services)." },
};

export type MetricBadge = { label: string; tone: ChipTone; hint: string };

/** Collect the present badges for a metric, in display order (Subject · TimeBasis · Role). */
export function metricBadges(m: Pick<MethodologyMetric, "subject" | "timeBasis" | "role">): MetricBadge[] {
  const out: MetricBadge[] = [];
  if (m.subject && SUBJECT_META[m.subject]) out.push(SUBJECT_META[m.subject]);
  if (m.timeBasis && TIME_BASIS_META[m.timeBasis]) out.push(TIME_BASIS_META[m.timeBasis]);
  if (m.role && ROLE_META[m.role]) out.push(ROLE_META[m.role]);
  return out;
}

// ---- Content shape ----------------------------------------------------------

export type MethodologyMetric = {
  /** Short scope/units line shown above the title (e.g. "% OF NAV", "VOL POINTS"). */
  key: string;
  /** The tile name as it appears on the dashboard. */
  label: string;
  /** LEGACY: what the number tells you. Kept as the "Tells you" body when `tellsYou` is absent. */
  why: string;
  /** LEGACY: how it's computed/read. Kept as the "How it's computed" body when `computed` is absent. */
  how: string;
  /** LEGACY single caveat pill. Superseded by `limits` once migrated. */
  tag?: string;

  // --- new rich schema (all optional; migrate panel-by-panel) ---
  subject?: SubjectBadge;
  timeBasis?: TimeBasisBadge;
  role?: RoleBadge;
  /** One-line plain-English definition. */
  whatItIs?: string;
  /** What it tells you / the decision it supports (overrides `why` when present). */
  tellsYou?: string;
  /** The inputs/drivers that move it. */
  movesIt?: string;
  /** How to read it: normal vs elevated, tone bands, an example value. */
  readIt?: string;
  /** The normal band you'd expect this to sit in (e.g. "2–8% of NAV", "0.85–0.95 contango"). */
  typicalRange?: string;
  /** Where the current reading sits vs normal — the z-score / percentile framing and what
   *  elevated/stress looks like (e.g. "live VIX z on the tile: 0 = trailing-252d avg, +1 elevated,
   *  +2 stress"). Lets you read a raw number as "how unusual is this right now". */
  gauge?: string;
  /** The method/formula (overrides `how` when present; rendered as collapsible "show the math"). */
  computed?: string;
  /** What it is NOT — explicit misreads to avoid. */
  limits?: string;
  /** Related tiles to read alongside (combinations). */
  readWith?: string;
  /** Data provenance (IBKR live / ORATS / NAV Fund Services / DB snapshot / model). */
  source?: string;
};

export type MethodologyPanel = {
  name: string;
  summary: string;
  metrics: MethodologyMetric[];
};

export type MethodologyContent = {
  intro: { eyebrow: string; title: string; subtitle: string; purpose: string; coreRule: string };
  toneLegends: Array<{ group: string; items: Array<{ label: string; meaning: string }> }>;
  panels: MethodologyPanel[];
  playbook: Array<{ title: string; detail: string }>;
  earlyReads: Array<{ title: string; detail: string }>;
  operations: {
    dataSources: Array<{ label: string; detail: string }>;
    freshness: Array<{ label: string; detail: string }>;
    governance: Array<{ label: string; detail: string }>;
    governanceNote: string;
  };
};
